import * as dayjs from 'dayjs';
import {
  PermanentSeatSelectionEntry,
  ArtificialSeatSelectionEntry, ISeatSelectionEntry, PassSeatSelectionEntry,
  ReservedSeatSelectionEntry,
  TemporarySeatSelectionEntry,
} from '../models/serializable/ticketing-state/seat-selection-entry.js';
import { EventSeatSelectionLock } from '../models/serializable/ticketing-state/event-seat-selection-lock.js';
import { TrueTime } from '../time/true-time.js';
import { isNullOrUndefined } from './null-safety.js';

/**
 * Admins should be able to bypass {@link ArtificialSeatSelectionEntry}s.
 */
export class AdminEventSeatSelectionLockHelper {
  private readonly entries: ISeatSelectionEntry[];

  constructor(
      lock: EventSeatSelectionLock,
  ) {
    this.entries = lock.entries;
  }

  removeAllTemporarySeatLocks(): void {
    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      if(TemporarySeatSelectionEntry.assertType(this.entries[ei])) {
        this.entries.splice(ei, 1);
      }
    }
  }

  isEverySeatPermanentlyLockedOrFree(seats: number[]): boolean {
    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      for(let si = this.entries[ei].seats.length - 1; si >= 0; si--) {
        for(let k = 0; k < seats.length; k++) {
          if(seats[k] !== this.entries[ei].seats[si]) {
            continue;
          }

          if(
            !PermanentSeatSelectionEntry.assertType(this.entries[ei]) &&
            !ReservedSeatSelectionEntry.assertType(this.entries[ei]) &&
            !ArtificialSeatSelectionEntry.assertType(this.entries[ei])
          ) {
            return false;
          }
        }
      }
    }

    return true;
  }

  tryCreateTemporarySeatLock(sessionId: string, seats: number[], now?: dayjs.Dayjs): boolean {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);

    const lockedSeats = new Set<number>();
    let entryIndex: number = -1;

    for(let ei = 0; ei < this.entries.length; ei++) {
      const entry = this.entries[ei];
      if(TemporarySeatSelectionEntry.assertType(entry) && entry.sessionId == sessionId) {
        entryIndex = ei;
        continue;
      }

      if(ArtificialSeatSelectionEntry.assertType(entry)) {
        continue;
      }

      addAllSeatsAsLocked(lockedSeats, entry.seats);
    }

    if(isAnySeatAlreadyLocked(lockedSeats, seats)) {
      return false;
    }

    const entry = TemporarySeatSelectionEntry.create({
      sessionId,
      seats,
      creationDate: now,
    });

    if(entryIndex >= 0) {
      this.entries[entryIndex] = entry;
    }
    else {
      this.entries.push(entry);
    }

    return true;
  }

  tryCreatePermanentSeatLock(sessionId: string, seats: number[], now?: dayjs.Dayjs): boolean {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);
    this.tryRemoveTemporarySeatLock(sessionId, seats, now);

    const lockedSeats = new Set<number>();
    let entryIndex: number = -1;

    for(let ei = 0; ei < this.entries.length; ei++) {
      const entry = this.entries[ei];
      if(PermanentSeatSelectionEntry.assertType(entry) && entry.sessionId == sessionId) {
        entryIndex = ei;
        continue;
      }

      if(ArtificialSeatSelectionEntry.assertType(entry)) {
        continue;
      }

      addAllSeatsAsLocked(lockedSeats, entry.seats);
    }

    if(isAnySeatAlreadyLocked(lockedSeats, seats)) {
      return false;
    }

    if(entryIndex >= 0) {
      lockedSeats.clear();
      addAllSeatsAsLocked(lockedSeats, this.entries[entryIndex].seats);
      addAllSeatsAsLocked(lockedSeats, seats);

      this.entries[entryIndex].seats.length = 0;
      this.entries[entryIndex].seats.push(...lockedSeats);
    }
    else {
      const entry = PermanentSeatSelectionEntry.create({
        sessionId,
        seats,
        creationDate: now,
      });

      this.entries.push(entry);
    }

    return true;
  }

  tryCreateReservedSeatLock(
    sessionId: string,
    seats: number[],
    reservationId: string,
    now?: dayjs.Dayjs,
    expirationDate?: dayjs.Dayjs,
  ): boolean {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);

    const lockedSeats = new Set<number>();
    let entryIndex: number = -1;

    for(let ei = 0; ei < this.entries.length; ei++) {
      const entry = this.entries[ei];
      if(
        ReservedSeatSelectionEntry.assertType(entry) &&
        entry.sessionId == sessionId &&
        entry.reservationId == reservationId
      ) {
        entryIndex = ei;
        continue;
      }

      if(ArtificialSeatSelectionEntry.assertType(entry)) {
        continue;
      }

      addAllSeatsAsLocked(lockedSeats, entry.seats);
    }

    if(isAnySeatAlreadyLocked(lockedSeats, seats)) {
      return false;
    }

    if(entryIndex >= 0) {
      lockedSeats.clear();
      addAllSeatsAsLocked(lockedSeats, this.entries[entryIndex].seats);
      addAllSeatsAsLocked(lockedSeats, seats);

      this.entries[entryIndex].seats.length = 0;
      this.entries[entryIndex].seats.push(...lockedSeats);
    }
    else {
      const entry = ReservedSeatSelectionEntry.create({
        sessionId,
        seats,
        reservationId,
        creationDate: now,
        expirationDate,
      });

      this.entries.push(entry);
    }

    return true;
  }

  tryRemovePermanentSeatLock(seats: number[], now?: dayjs.Dayjs): void {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);

    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      if(!PermanentSeatSelectionEntry.assertType(this.entries[ei])) {
        continue;
      }

      for(let si = this.entries[ei].seats.length - 1; si >= 0; si--) {
        for(let k = 0; k < seats.length; k++) {
          if(seats[k] == this.entries[ei].seats[si]) {
            this.entries[ei].seats.splice(si, 1);
            break;
          }
        }
      }

      if(this.entries[ei].seats.length == 0) {
        this.entries.splice(ei, 1);
      }
    }
  }

  tryRemoveTemporarySeatLock(sessionId: string, seats: number[], now?: dayjs.Dayjs): number[] {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);

    const seatSet = new Set(seats);
    const removedSeatIds = new Set<number>();

    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      const entry = this.entries[ei];
      if(!TemporarySeatSelectionEntry.assertType(entry) || entry.sessionId !== sessionId) {
        continue;
      }

      for(let si = entry.seats.length - 1; si >= 0; si--) {
        const seatId = entry.seats[si];
        if(seatSet.has(seatId)) {
          entry.seats.splice(si, 1);
          removedSeatIds.add(seatId);
        }
      }

      if(entry.seats.length == 0) {
        this.entries.splice(ei, 1);
      }
    }

    return Array.from(removedSeatIds);
  }

  tryRemoveReservedSeatLock(seats: number[], now?: dayjs.Dayjs): void {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);

    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      if(!ReservedSeatSelectionEntry.assertType(this.entries[ei])) {
        continue;
      }

      for(let si = this.entries[ei].seats.length - 1; si >= 0; si--) {
        for(let k = 0; k < seats.length; k++) {
          if(seats[k] == this.entries[ei].seats[si]) {
            this.entries[ei].seats.splice(si, 1);
            break;
          }
        }
      }

      if(this.entries[ei].seats.length == 0) {
        this.entries.splice(ei, 1);
      }
    }
  }

  tryCreatePassSeatLock(templateId: string, passSeats: number[], passLockSeats: number[], now?: dayjs.Dayjs): void {
    now ??= TrueTime.now();
    // passLockSeats ??= [];

    this.unlockAllExpiredEntries(now);

    let entryIndex: number = -1;

    for(let ei = 0; ei < this.entries.length; ei++) {
      const entry = this.entries[ei];
      if(PassSeatSelectionEntry.assertType(entry) && entry.templateId == templateId) {
        entryIndex = ei;
        break;
      }
    }

    if(entryIndex >= 0) {
      const entry = this.entries[entryIndex] as PassSeatSelectionEntry;
      const updatedSeats = new Set<number>();
      const updatedLocks = new Set<number>();

      addAllSeatsAsLocked(updatedSeats, entry.seats);
      addAllSeatsAsLocked(updatedLocks, entry.locks);

      // If a seat was previously locked but now has been converted to a pass seat, we remove it from the locks list.
      for(const seatId of passSeats) {
        updatedLocks.delete(seatId);
      }

      addAllSeatsAsLocked(updatedSeats, passSeats);
      addAllSeatsAsLocked(updatedSeats, passLockSeats);

      addAllSeatsAsLocked(updatedLocks, passLockSeats);

      entry.seats.length = 0;
      entry.seats.push(...updatedSeats);

      entry.locks.length = 0;
      entry.locks.push(...updatedLocks);
    }
    else {
      const entry = PassSeatSelectionEntry.create({
        templateId: templateId,
        seats: [...passSeats, ...passLockSeats],
        locks: [...passLockSeats],
        creationDate: now,
      });

      this.entries.push(entry);
    }
  }

  tryRemovePassSeatLock(templateId: string, passSeats: number[], passLockSeats: number[], now?: dayjs.Dayjs): void {
    now ??= TrueTime.now();
    // passLockSeats ??= [];

    this.unlockAllExpiredEntries(now);

    let entryIndex: number = -1;

    for(let ei = 0; ei < this.entries.length; ei++) {
      const entry = this.entries[ei];
      if(PassSeatSelectionEntry.assertType(entry) && entry.templateId == templateId) {
        entryIndex = ei;
        break;
      }
    }

    if(entryIndex >= 0) {
      const entry = this.entries[entryIndex] as PassSeatSelectionEntry;
      for(let si = entry.seats.length - 1; si >= 0; si--) {
        for(let k = 0; k < passSeats.length; k++) {
          if(passSeats[k] == entry.seats[si]) {
            entry.seats.splice(si, 1);
            break;
          }
        }
      }

      for(let si = entry.seats.length - 1; si >= 0; si--) {
        for(let k = 0; k < passLockSeats.length; k++) {
          if(passLockSeats[k] == entry.seats[si]) {
            entry.seats.splice(si, 1);
            break;
          }
        }
      }

      for(let li = entry.locks.length - 1; li >= 0; li--) {
        for(let k = 0; k < passLockSeats.length; k++) {
          if(passLockSeats[k] == entry.locks[li]) {
            entry.locks.splice(li, 1);
            break;
          }
        }
      }

      if(entry.seats.length == 0) {
        this.entries.splice(entryIndex, 1);
      }
    }
  }

  setArtificialSeatSelection(newSelection?: ArtificialSeatSelectionEntry | null): void {
    const existingSelectionIndex = this.entries
        .findIndex((entry) => ArtificialSeatSelectionEntry.assertType(entry));

    if(isNullOrUndefined(newSelection)) {
      if(existingSelectionIndex >= 0) {
        this.entries.splice(existingSelectionIndex, 1);
      }
    }
    else {
      if(existingSelectionIndex >= 0) {
        this.entries[existingSelectionIndex] = newSelection;
      }
      else {
        this.entries.push(newSelection);
      }
    }
  }

  private unlockAllExpiredEntries(now: dayjs.Dayjs): void {
    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      if(this.entries[ei].isExpired(now)) {
        this.entries.splice(ei, 1);
      }
    }
  }

}

/**
 * Customers should be subject to {@link ArtificialSeatSelectionEntry}s.
 */
export class CustomerEventSeatSelectionLockHelper {
  private readonly entries: ISeatSelectionEntry[];

  constructor(
      lock: EventSeatSelectionLock,
  ) {
    this.entries = lock.entries;
  }

  tryCreateTemporarySeatLock(sessionId: string, seats: number[], now?: dayjs.Dayjs): boolean {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);

    const lockedSeats = new Set<number>();
    let entryIndex: number = -1;

    for(let ei = 0; ei < this.entries.length; ei++) {
      const entry = this.entries[ei];
      if(TemporarySeatSelectionEntry.assertType(entry) && entry.sessionId == sessionId) {
        entryIndex = ei;
        continue;
      }

      addAllSeatsAsLocked(lockedSeats, entry.seats);
    }

    if(isAnySeatAlreadyLocked(lockedSeats, seats)) {
      return false;
    }

    const entry = TemporarySeatSelectionEntry.create({
      sessionId,
      seats,
      creationDate: now,
    });

    if(entryIndex >= 0) {
      this.entries[entryIndex] = entry;
    }
    else {
      this.entries.push(entry);
    }

    return true;
  }

  private unlockAllExpiredEntries(now: dayjs.Dayjs): void {
    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      if(this.entries[ei].isExpired(now)) {
        this.entries.splice(ei, 1);
      }
    }
  }
}

function addAllSeatsAsLocked(allLockedSeats: Set<number>, seats: number[]): void {
  for(let i = 0; i < seats.length; i++) {
    allLockedSeats.add(seats[i]);
  }
}

function isAnySeatAlreadyLocked(allLockedSeats: Set<number>, seats: number[]): boolean {
  for(let i = 0; i < seats.length; i++) {
    if(allLockedSeats.has(seats[i])) {
      return true;
    }
  }

  return false;
}
